import React, { useCallback, useEffect, useState } from "react";
import { marketplaceService, MarketplaceListingSummary } from "../../services/marketplaceService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStore, faMagnifyingGlass, faFire, faCartPlus } from "@fortawesome/free-solid-svg-icons";
import { useToast } from "../../components/ui/Toast";
import { Button, Input, LoadingState, EmptyState } from "../../components/ui";
import MarketplaceListingDetailsView from "./MarketplaceListingDetailsView";
import { formatMoney } from "../../utils/money";

const Marketplace: React.FC = () => {
  const [listings, setListings] = useState<MarketplaceListingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedListingId, setSelectedListingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const toast = useToast();

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await marketplaceService.getListings();
      setListings(data);
    } catch (error) {
      console.error("Failed to fetch listings:", error);
      toast.showToast({ type: "error", message: "Failed to load marketplace listings" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchListings();
  }, [fetchListings]);

  const filteredListings = listings.filter((listing) => {
    if (!searchQuery.trim()) return true;
    const lowerQuery = searchQuery.toLowerCase();
    return (
      listing.title.toLowerCase().includes(lowerQuery) ||
      (listing.description && listing.description.toLowerCase().includes(lowerQuery))
    );
  });

  return (
    <div className="bg-zinc-950 text-zinc-100 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-zinc-900 border-b border-zinc-800 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faStore} className="text-amber-500 text-xl" />
          <h1 className="text-xl font-light tracking-widest uppercase font-serif">Marketplace</h1>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10"
            />
            <Input
              type="text"
              placeholder="Search bundles..."
              className="pl-10 rounded-full w-64"
              variant="filled"
              size="sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Categories */}
        <div className="flex flex-wrap gap-3 mb-10">
          <Button
            variant="primary"
            size="sm"
            className="rounded-full px-5 text-xs uppercase tracking-wider font-bold"
          >
            All Content
          </Button>
        </div>

        <h3 className="text-xl font-bold mb-8 flex items-center gap-3 font-serif tracking-wide text-white">
          <FontAwesomeIcon icon={faFire} className="text-orange-500" /> Trending Now
        </h3>

        {loading ? (
          <div className="py-20">
            <LoadingState message="Summoning marketplace content..." variant="spinner" size="lg" />
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="py-20">
            <EmptyState
              title={searchQuery ? "No Bundles Found" : "The Bazaar is Quiet"}
              description={
                searchQuery
                  ? `No bundles matched your search for "${searchQuery}".`
                  : "No marketplace listings available at the moment. Check back soon for new treasures."
              }
              icon={<FontAwesomeIcon icon={faStore} className="text-4xl text-zinc-700" />}
              className="bg-zinc-900/30 rounded-2xl border border-dashed border-zinc-800 py-16"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredListings.map((listing) => (
              <div
                key={listing.id}
                onClick={() => setSelectedListingId(listing.id)}
                className="group bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800 hover:border-amber-500/50 transition-all cursor-pointer shadow-lg hover:shadow-amber-500/10"
              >
                <div className="relative h-48 bg-zinc-800 flex items-center justify-center">
                  <FontAwesomeIcon icon={faStore} className="text-4xl text-zinc-700 opacity-30" />
                  <div className="absolute top-3 right-3 bg-gradient-to-br from-amber-400 to-amber-600 text-black text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter shadow-md">
                    Marketplace
                  </div>
                </div>
                <div className="p-5 flex flex-col min-h-[160px]">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold group-hover:text-amber-400 transition-colors line-clamp-1 text-white">
                      {listing.title}
                    </h4>
                    <span className="text-amber-400 font-bold whitespace-nowrap ml-2">
                      {listing.priceCents > 0
                        ? formatMoney(listing.priceCents, listing.currency)
                        : "Free"}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-xs mb-6 line-clamp-2 leading-relaxed">
                    {listing.description || "A premium bundle for your chronicles."}
                  </p>
                  <div className="flex items-center justify-between border-t border-zinc-800 pt-4 mt-auto">
                    <div className="flex gap-2">
                      <span className="text-[10px] bg-zinc-800 px-2 py-0.5 rounded text-zinc-400 uppercase tracking-widest font-bold">
                        Bundle
                      </span>
                    </div>
                    <span
                      aria-hidden="true"
                      className="text-zinc-400 group-hover:text-amber-400 transition-colors"
                    >
                      <FontAwesomeIcon icon={faCartPlus} />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Details View Modal */}
      {selectedListingId && (
        <MarketplaceListingDetailsView
          listingId={selectedListingId}
          onClose={() => setSelectedListingId(null)}
          onActivated={() => {
            fetchListings();
          }}
        />
      )}
    </div>
  );
};

export default Marketplace;
